import { Button } from 'components/ui/button';
import { ArrowLeftIcon, MoreVertical } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'store';
import SaveImageModal from '../components/modals/SaveImageModal';
import PromotionalBanner from '../components/PromotionalBanner';
import { handleDownloadImage } from 'lib/utils';
import EditLogoForm from '../components/EditLogoForm';
import AuthRequiredModal from '../components/modals/AuthRequiredModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useState } from 'react';
import RegenerateModal from '../components/modals/RegenerateModal';

export default function GeneratedLogoResult() {
  const { fileName, tempLogoUrl, isSaved } = useSelector(
    (state: RootState) => state.logoGen
  );
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalType, setAuthModalType] = useState<'save' | 'export'>('save');
  const [isExporting, setIsExporting] = useState(false);

  const handleSave = () => {
    if (!isAuthenticated) {
      setAuthModalType('save');
      setShowAuthModal(true);
    } else {
      setShowSaveModal(true);
    }
  };

  const handleExport = async () => {
    if (!isAuthenticated) {
      setAuthModalType('export');
      setShowAuthModal(true);
    } else {
      setIsExporting(true);
      try {
        await handleDownloadImage(tempLogoUrl, fileName);
      } finally {
        setIsExporting(false);
      }
    }
  };

  return (
    <div className="h-full text-text-primary">
      {/* Mobile Layout (below lg) */}
      <div className="lg:hidden flex flex-col h-full bg-background-main lg:bg-transparent">
        {/* Header */}
        <div className="p-3 px-4 border-b flex items-center gap-2">
          <Link
            to={'/text-to-logo/get-started'}
            className="flex items-center gap-3"
          >
            <ArrowLeftIcon color="#846CEC" size={20} />
          </Link>
          <div className="flex items-center gap-2">
            <p className="text-lg font-medium ">Logo Generator{' / '}</p>
            <p className="text-lg font-medium truncate">{fileName}</p>
          </div>

          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="text-primary" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='bg-background text-text-primary'>
                <DropdownMenuItem onSelect={() => setShowRegenerateModal(true)}>
                  Regenerate Logo
                </DropdownMenuItem>               
                {!isSaved && (
                  <DropdownMenuItem onClick={handleSave}>
                    Save
                  </DropdownMenuItem>
                )}                
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto">
          <PromotionalBanner />

          <div className="p-4 lg:p-6 w-full flex items-center justify-center">
            <img
              src={tempLogoUrl}
              className="w-full h-auto object-cover rounded-lg lg:rounded-md"
            />
          </div>

          <div className="pb-32">
            <EditLogoForm />
          </div>
        </div>

        {/* Fixed Bottom Buttons */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background-main border-t flex gap-4">
          <Button
            variant="customSecondary"
            className="flex-1 bg-secondary"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>

        {/* Modals */}
        {isAuthenticated && (
            <SaveImageModal
              isOpen={showSaveModal}
              onOpenChange={setShowSaveModal}
            />    
        )}
        <RegenerateModal
          isOpen={showRegenerateModal}
          onOpenChange={setShowRegenerateModal}
        />
      </div>

      {/* Desktop Layout (lg and above) */}
      <div className="hidden lg:grid grid-cols-12 h-full gap-4">
        <div className="bg-background-main w-full col-span-9 overflow-auto rounded-md">
          <div className="p-3 px-4 border-b flex items-center gap-2">
            <Link
              to={'/text-to-logo/get-started'}
              className="flex items-center gap-3"
            >
              <ArrowLeftIcon color="#846CEC" size={20} />
            </Link>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-medium ">Logo Generator{' / '}</p>
              <p className="text-2xl font-medium">{fileName}</p>
            </div>

            <div className="flex items-center gap-4 ml-auto">
              <Button
                variant={'customSecondary'}
                className="bg-secondary"
                size={'sm'}
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
              {!isSaved && (
                <Button
                  variant={'customPrimary'}
                  size={'sm'}
                  onClick={handleSave}
                >
                  Save
                </Button>
              )}
            </div>
          </div>
          <PromotionalBanner />

          <div className="p-12 w-full flex items-center justify-center">
            <img
              src={tempLogoUrl}
              className="w-[35vw] h-auto object-cover rounded-md"
            />
          </div>
        </div>
        <EditLogoForm />
      </div>

      {/* Auth Required Modal */}
      <AuthRequiredModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        type={authModalType}
      />
    </div>
  );
}